/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction, KeyValuePair } from '../../helpers/jsHelpers/types';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';

import { generateToken } from '../../helpers/crypto/crypto';
import { buildDeviceUrl } from '../../helpers/dms/helper';
import { isRunningOnLocalhost } from '../../helpers/jsHelpers/environment';
import Localizer from '../../helpers/localization';
import { StorageKeys } from '../../helpers/localStorage/keys';
import LocalStorageManager from '../../helpers/localStorage/localStorageManager';
import { Messenger, MessengerEvents } from '../../helpers/messenger';
import { PendingOperationKind, clearPendingOperation } from '../../helpers/rights/pendingOperations';
import { registerApplication } from '../../redux/appRegistration/actions';
import { RegistrationType, type UserDeviceInfoType } from '../../redux/appRegistration/types/types';
import ButtonFX from '../buttons/ButtonFX';
import './RegistrationView.css';
import { ApplicationState } from './ApplicationContainer';
import { parseUrl } from './helper';

// Time allowed for login page to load (in milliseconds)
const LOAD_TIMEOUT = 60_000;

type ExtraCrmData = {
  showPods?: boolean,
  showSignin?: boolean,
  showSignup?: boolean,
  showSubscribe?: boolean,
};

type ReduxRegistrationReducerStateType = {
  +applicationId: string,
  +authDeviceUrl: string,
  +authenticationToken: string | null,
  +deviceId: string,
  +deviceKey: string,
  +isRegisteredAsGuest: boolean,
  +loginUrl: string,
  +logoutUrl: string,
  +podsWebUrl: string,
  +signupUrl: string,
  +subscriberId: string,
  +subscribeWebUrl: string,
  +upgradeDeviceUrl: string,
  +utmParameters: KeyValuePair<string>,
};

type ReduxRegistrationDispatchToPropsType = {
  +localRegisterApplication: (userDeviceInfo: UserDeviceInfoType, asGuest: boolean) => void,
};

type DefaultProps = {
  +pendingOperationType?: PendingOperationKind,
};

type RegistrationPropType = {
  ...DefaultProps,
  +applicationState: ApplicationState,
  +closeDebugPanelCallback: BasicFunction,
  +crmLoadTimeoutCallback: BasicFunction,
  +hideHeaderCallback: BasicFunction,
  +highlightedCommercialOffers: Array<string>,
  +isDebugPanelVisible: boolean,
  +registrationFailureCallback: BasicFunction,
};

type CompleteRegistrationPropType = {
  ...RegistrationPropType,
  ...ReduxRegistrationReducerStateType,
  ...ReduxRegistrationDispatchToPropsType,
};

type RegistrationStateType = {
  crmUrl: string | null,
  overriddenAppConf: string,
  selectedAppConf: string,
};

const InitialState = Object.freeze({
  crmUrl: null,
  overriddenAppConf: '',
  selectedAppConf: '',
});

class RegistrationView extends React.PureComponent<CompleteRegistrationPropType, RegistrationStateType> {
  loadTimer: TimeoutID | null;

  static defaultProps: DefaultProps = {
    pendingOperationType: undefined,
  };

  constructor(props: CompleteRegistrationPropType) {
    super(props);

    this.loadTimer = null;
    this.state = { ...InitialState };
  }

  componentDidMount() {
    const { crmLoadTimeoutCallback } = this.props;

    window.addEventListener('message', this.handleMessagePosted, {
      capture: true,
      passive: true,
    });

    const overriddenAppConf = LocalStorageManager.loadString(StorageKeys.OverriddenAppConf, '');
    if (overriddenAppConf !== '') {
      this.setState({
        overriddenAppConf,
        selectedAppConf: overriddenAppConf,
      });
    }

    this.loadTimer = setTimeout(crmLoadTimeoutCallback, LOAD_TIMEOUT);
    this.buildCrmUrl();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMessagePosted, {
      capture: true,
      passive: true,
    });

    this.resetLoadTimer();
  }

  resetLoadTimer = () => {
    const { loadTimer } = this;

    if (loadTimer) {
      clearTimeout(loadTimer);
      this.loadTimer = null;
    }
  };

  clearCrmData = () => {
    LocalStorageManager.delete(StorageKeys.CrmData);
  };

  // Store data in local storage for CRM pages
  storeCrmData = (extraData: ExtraCrmData) => {
    const {
      applicationId,
      authDeviceUrl,
      authenticationToken: token,
      deviceKey,
      highlightedCommercialOffers,
      isRegisteredAsGuest,
      logoutUrl,
      pendingOperationType,
      subscriberId,
      upgradeDeviceUrl,
      utmParameters,
    } = this.props;

    // $FlowFixMe[cannot-spread-indexer]
    LocalStorageManager.save(StorageKeys.CrmData, {
      ...extraData,
      ...utmParameters,
      anonymous: isRegisteredAsGuest,
      applicationId,
      authDeviceUrl,
      deviceKey,
      highlightedCommercialOffers,
      logoutUrl,
      pendingOperationType,
      subscriberId,
      token,
      upgradeDeviceUrl,
    });
  };

  buildCrmUrl = () => {
    const { applicationState, authenticationToken, deviceId, isRegisteredAsGuest, loginUrl, podsWebUrl, signupUrl, subscribeWebUrl } = this.props;

    // Display sign-in page (most used case)
    let crmUrl = loginUrl;

    this.clearCrmData();

    if (applicationState === ApplicationState.SignUpDisplayed && signupUrl) {
      // Display sign-up page
      this.storeCrmData({ showSignup: true });
      crmUrl = signupUrl;
    } else if (applicationState === ApplicationState.SubscribeDisplayed && subscribeWebUrl) {
      // Display subscribe page
      this.storeCrmData({ showSubscribe: true });

      // If not logged in, user will be prompted to sign in
      if (authenticationToken && !isRegisteredAsGuest) {
        crmUrl = subscribeWebUrl;
      } else {
        crmUrl = signupUrl;
      }
    } else if (applicationState === ApplicationState.PodsDisplayed && podsWebUrl) {
      // Display pods page
      this.storeCrmData({ showPods: true });
      crmUrl = podsWebUrl;
    } else {
      // Display sign-in page
      this.storeCrmData({ showSignin: true });
    }

    this.setState({ crmUrl: buildDeviceUrl(deviceId, crmUrl) });
  };

  handleMessagePosted = (e: SyntheticInputEvent<HTMLElement>) => {
    const { deviceId, hideHeaderCallback, localRegisterApplication, registrationFailureCallback } = this.props;
    const { crmUrl } = this.state;
    const { data } = e;

    if (typeof data !== 'string' || data === 'closed' || data.startsWith('setImmediate')) {
      // Unwanted data or special mobile case
      return;
    }

    /*
     * Temporarily commented because of too many different domains for myVF
     *
     * if (!isSameDomain(process.env.REACT_APP_IFRAME_DOMAIN_ORIGIN, origin) && !(/^https?:\/\/localhost:3000$/ui).test(origin)) {
     *   // Message does not come from a trusted source
     *   return;
     * }
     */

    if (data === 'reloadtop') {
      this.resetLoadTimer();
      if (crmUrl && !isRunningOnLocalhost()) {
        window.location.replace(crmUrl);
      } else {
        hideHeaderCallback();
      }
      return;
    }

    if (data === 'cancel') {
      // Sign-in was canceled: go back to guest mode
      this.resetLoadTimer();

      // User closed sign-in page: no need to retry to play
      clearPendingOperation();

      Messenger.emit(MessengerEvents.SHOW_GUEST_MODE);
      return;
    }

    if (data === 'loaded') {
      this.resetLoadTimer();
      return;
    }

    if (data === 'accountcreated') {
      Messenger.emit(MessengerEvents.ACCOUNT_CREATED);
      return;
    }

    if (data === 'subscribed') {
      Messenger.emit(MessengerEvents.SUBSCRIBED);
      Messenger.emit(MessengerEvents.REFRESH_AUTHENTICATION_TOKEN);
      return;
    }

    const defaultUserDeviceInfo: UserDeviceInfoType = {
      anonymous: false,
      applicationId: '',
      authDeviceUrl: '',
      deviceKey: '',
      error: null,
      logoutUrl: '',
      subscriberId: '',
      upgradeDeviceUrl: '',
    };

    const userDeviceInfo = parseUrl(data, defaultUserDeviceInfo);
    const { anonymous, applicationId, authDeviceUrl, deviceKey, error, subscriberId, upgradeDeviceUrl } = userDeviceInfo;

    if ((typeof error === 'string' && error !== '') || !applicationId || !authDeviceUrl || !deviceKey || !subscriberId || !upgradeDeviceUrl) {
      registrationFailureCallback();
      return;
    }

    // User successfully signed in
    generateToken(deviceId, userDeviceInfo).then((userDeviceInfoJwt) => {
      LocalStorageManager.save(StorageKeys.UserDeviceInfo, userDeviceInfoJwt);
      LocalStorageManager.save(StorageKeys.RegisteredAsGuest, anonymous);

      localRegisterApplication(userDeviceInfo, anonymous);
    });
  };

  handleCloseDebugPanelOnClick = () => {
    const { closeDebugPanelCallback } = this.props;

    closeDebugPanelCallback();
  };

  handleRevertAppConfOnClick = () => {
    // Remove overridden AppConf from local storage, then reload app
    LocalStorageManager.delete(StorageKeys.OverriddenAppConf);
    window.location.reload();
  };

  handleApplyAppConfOnClick = () => {
    const { selectedAppConf } = this.state;

    // Store overridden AppConf in local storage, then reload app
    LocalStorageManager.save(StorageKeys.OverriddenAppConf, selectedAppConf);
    window.location.reload();
  };

  handleAppConfUrlOnChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const {
      currentTarget: { value },
    } = event;

    this.setState({ selectedAppConf: value });
  };

  renderOverriddenAppConf = (): React.Node => {
    const { isDebugPanelVisible } = this.props;
    const { overriddenAppConf } = this.state;

    if (!isDebugPanelVisible && overriddenAppConf === '') {
      return null;
    }

    return (
      <div className='overriddenAppConf'>
        <div className='label'>AppConf URL:</div>
        <div className='value'>{overriddenAppConf || 'unchanged'}</div>
      </div>
    );
  };

  renderAppConf = (): React.Node => {
    const { isDebugPanelVisible } = this.props;
    const { selectedAppConf } = this.state;

    if (!isDebugPanelVisible) {
      return null;
    }

    return (
      <>
        <div className='separator' />
        <div className='row'>
          <div className='label'>AppConf URL:</div>
          <input onChange={this.handleAppConfUrlOnChange} placeholder='Enter an AppConf URL' type='text' value={selectedAppConf} />
        </div>
        <div className='row buttons'>
          <ButtonFX onClick={this.handleCloseDebugPanelOnClick}>Close</ButtonFX>
          <ButtonFX onClick={this.handleRevertAppConfOnClick}>Revert AppConf</ButtonFX>
          <ButtonFX onClick={this.handleApplyAppConfOnClick}>Apply AppConf</ButtonFX>
        </div>
      </>
    );
  };

  renderDebugPanel = (): React.Node => {
    const overriddenAppConfElt = this.renderOverriddenAppConf();
    const appConfElt = this.renderAppConf();

    if (!overriddenAppConfElt && !appConfElt) {
      return null;
    }

    return (
      <div className={clsx('debugPanel', overriddenAppConfElt && !appConfElt && 'reduced')}>
        {overriddenAppConfElt}
        {appConfElt}
      </div>
    );
  };

  render(): React.Node {
    const { crmUrl } = this.state;

    if (!crmUrl) {
      return null;
    }

    /*
     * Reason for eslint-disable: CRM requires strict access to localStorage and JavaScript execution
     * across shared brand domains, making the sandbox attribute functionally incompatible
     */
    return (
      <div className='registrationFrameContainer'>
        {this.renderDebugPanel()}
        {/* eslint-disable react/iframe-missing-sandbox */}
        <iframe className='registrationFrame' src={crmUrl} title={Localizer.localize('accessibility.navigation.registration_page')} />
      </div>
    );
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxRegistrationReducerStateType => {
  return {
    applicationId: state.appRegistration.applicationId,
    authDeviceUrl: state.appRegistration.authDeviceUrl,
    authenticationToken: state.appRegistration.authenticationToken,
    deviceId: state.appRegistration.deviceId,
    deviceKey: state.appRegistration.deviceKey,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    loginUrl: state.appConfiguration.configuration.loginUrl ?? '',
    logoutUrl: state.appRegistration.logoutUrl,
    podsWebUrl: state.appConfiguration.configuration.podsWebUrl ?? '',
    signupUrl: state.appConfiguration.configuration.signupUrl ?? '',
    subscriberId: state.appRegistration.subscriberId,
    subscribeWebUrl: state.appConfiguration.configuration.subscribeWebUrl ?? '',
    upgradeDeviceUrl: state.appRegistration.upgradeDeviceUrl,
    utmParameters: state.appConfiguration.utmParameters,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxRegistrationDispatchToPropsType => {
  return {
    localRegisterApplication: (userDeviceInfo: UserDeviceInfoType, asGuest: boolean): void => dispatch(registerApplication(userDeviceInfo, asGuest)),
  };
};

const Registration: React.ComponentType<RegistrationPropType> = connect(mapStateToProps, mapDispatchToProps)(RegistrationView);

export default Registration;
